import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Users, 
    Scissors, 
    Factory, 
    Truck, 
    AlertTriangle, 
    AlertCircle,
    Info,
    TrendingUp,
    DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#FBBF24', '#22C55E', '#EF4444', '#8B5CF6', '#06B6D4'];

export const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [alertas, setAlertas] = useState([]);
    const [maquiladores, setMaquiladores] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, alertasRes, maquiladoresRes] = await Promise.all([
                axios.get(`${API_URL}/api/dashboard/stats`),
                axios.get(`${API_URL}/api/dashboard/alertas`),
                axios.get(`${API_URL}/api/dashboard/maquiladores`)
            ]);
            setStats(statsRes.data);
            setAlertas(alertasRes.data);
            setMaquiladores(maquiladoresRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Error al cargar datos del dashboard');
        } finally {
            setLoading(false);
        }
    };

    const getAlertIcon = (tipo) => {
        switch (tipo) {
            case 'error': return <AlertCircle className="w-5 h-5 text-red-400" />;
            case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
            default: return <Info className="w-5 h-5 text-amber-400" />;
        }
    };

    const getAlertClass = (tipo) => {
        switch (tipo) {
            case 'error': return 'alert-error';
            case 'warning': return 'alert-warning';
            default: return 'alert-info';
        }
    };

    const formatProduccionData = () => {
        if (!stats?.produccion_diaria) return [];
        return Object.entries(stats.produccion_diaria).map(([fecha, cantidad]) => ({
            fecha: fecha.split('-').slice(1).join('/'),
            cantidad
        })).slice(-7);
    };

    const formatMaquiladoresData = () => {
        return maquiladores.map(m => ({
            name: m._id || 'Sin nombre',
            pendientes: m.pendientes,
            completados: m.completados,
            con_faltantes: m.con_faltantes
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="stat-card card-hover" data-testid="stat-empleados">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="stat-label">Empleados Activos</p>
                                <p className="stat-value">{stats?.total_empleados || 0}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
                                <Users className="w-6 h-6 text-amber-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card card-hover" data-testid="stat-cortes">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="stat-label">Cortes Activos</p>
                                <p className="stat-value">{stats?.total_cortes || 0}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                                <Scissors className="w-6 h-6 text-emerald-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card card-hover" data-testid="stat-maquila">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="stat-label">Maquila Pendiente</p>
                                <p className="stat-value">{stats?.maquila_pendiente || 0}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                                <Factory className="w-6 h-6 text-yellow-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card card-hover" data-testid="stat-deuda">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="stat-label">Deuda Proveedores</p>
                                <p className="stat-value text-2xl">
                                    ${(stats?.deuda_total_proveedores || 0).toLocaleString('es-MX')}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-red-400/10 flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-red-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Production Chart */}
                <Card className="card" data-testid="chart-produccion">
                    <CardHeader className="card-header">
                        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
                            <TrendingUp className="w-5 h-5 text-amber-400" />
                            Producción Diaria
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="card-body">
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={formatProduccionData()}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                                    <XAxis dataKey="fecha" stroke="#71717A" fontSize={12} />
                                    <YAxis stroke="#71717A" fontSize={12} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#18181B', 
                                            border: '1px solid #27272A',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Bar dataKey="cantidad" fill="#FBBF24" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Maquiladores Chart */}
                <Card className="card" data-testid="chart-maquiladores">
                    <CardHeader className="card-header">
                        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
                            <Factory className="w-5 h-5 text-amber-400" />
                            Estado por Maquilador
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="card-body">
                        <div className="h-[300px]">
                            {maquiladores.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={maquiladores.map(m => ({
                                                name: m._id || 'Sin nombre',
                                                value: m.total_entregas
                                            }))}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {maquiladores.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full text-zinc-400">
                                    No hay datos de maquiladores
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Alerts Section */}
            <Card className="card" data-testid="alertas-section">
                <CardHeader className="card-header">
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                        Alertas del Sistema
                    </CardTitle>
                </CardHeader>
                <CardContent className="card-body">
                    {alertas.length > 0 ? (
                        <div className="space-y-3">
                            {alertas.map((alerta, index) => (
                                <div
                                    key={index}
                                    className={`alert ${getAlertClass(alerta.tipo)}`}
                                    data-testid={`alerta-${index}`}
                                >
                                    {getAlertIcon(alerta.tipo)}
                                    <span className="flex-1">{alerta.mensaje}</span>
                                    <span className="text-xs opacity-70 capitalize">{alerta.modulo}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-zinc-400">
                            <Info className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No hay alertas en este momento</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
